import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, ButtonBase, makeStyles, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import _, { isEmpty } from 'lodash';
import StyleButton from '../../../../components/StyleButton';
import SearchInput from '../../../../components/SearchInput';
import { i18n } from '../../../../language';
import { AddIcon, EditIcon } from '../../../../components/Icon';
import Results from '../Results';
import TableUI from '../../../../components/TableUI';
import { systemAPI, equipmentAPI } from '../../../../apis';
import AutoCompleteMultiCheckbox from '../../../../components/AutoCompleteMultiCheckbox';
import { authNameE } from '../../../../utils/userFuncs';
import NewEquipmentCategoryDialog from './NewEquipmentCategoryDialog';
import UpdateEquipmentCategoryDialog from './UpdateEquipmentCategoryDialog';
import MultiThresholdButton from './MultiThresholdButton';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  headText: {
    fontSize: '1rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
    color: '#3e3e3e',
    marginRight: '0.5rem',
  },
  wardAutoSelect: {
    marginRight: '0.75rem',
  },
  borderB: {
    padding: '0',
    borderBottom: 'solid 1px #ddd',
  },
  resultsText: {
    padding: '1.094rem 0',
  },
  editButton: {
    backgroundColor: '#4c93ff',
    borderRadius: '0.625rem',
    width: '2.25rem',
    height: '2.25rem',
  },
  editIcon: {
    fontSize: '1rem',
    color: '#ffffff',
  },
  resultsBox: {
    padding: '0',
  },
  reset: {
    fontSize: '1rem',
    fontWeight: '500',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#1973e6',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));
const initFilter = {
  search: '',
  category: [],
  criticality: [],
  ward: [],
};
const CategoryTab = () => {
  const classes = useStyles();
  const [categoryListData, setCategoryListData] = useState(null);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [criticalityCategoryList] = useState([
    { text: i18n.t('SETTING_CRITICALITY_NORMAL'), value: 1 },
    { text: i18n.t('SETTING_CRITICALITY_HIGH'), value: 2 },
  ]);
  const [count, setCount] = useState(null);
  const [, setBarcodeListenerCtrl] = useState(true);
  const [newEquipmentCategoryOpen, setNewEquipmentCategoryOpen] = useState(false);
  const [editLoanRequestDialog, setEditLoanRequestDialog] = useState();
  const [categories, setCategories] = useState(null);
  const [wardArr, setWardArr] = useState(null);
  const [filter, setFilter] = useState(initFilter);
  const roleMap = useSelector((state) => state.roleMap);
  const pageSize = 20;
  const pageNo = 1;
  const tableParams = useMemo(
    () => ({
      userId: loginInfo.userId,
      corpId: loginInfo.corpID,
      hospId: loginInfo.hospitalId,
      pageInfo: {
        pageNo: pageNo,
        pageSize: pageSize,
      },
      keyword: filter.search,
      categoryIds: _.map(filter.category, (item) => item.value),
      critical: _.map(filter.criticality, (item) => item.value),
      wardIds: _.map(filter.ward, (item) => item.value),
    }),
    [loginInfo, filter]
  );
  // 获取table数据
  const getEquipmentCategoryList = useCallback(async () => {
    try {
      if (!tableParams.corpId) {
        return;
      }

      const { result } = await systemAPI.getEquipmentCategoryList(tableParams);
      setCategoryListData(result);
    } catch (error) {
      console.log(error);
    }
  }, [tableParams]);
  useEffect(() => {
    (async () => {
      await getEquipmentCategoryList();
    })();
  }, [getEquipmentCategoryList]);

  const inputOnFocus = useCallback(() => {
    setBarcodeListenerCtrl(false);
  }, []);

  const inputOnBlur = useCallback(() => {
    setBarcodeListenerCtrl(true);
  }, []);
  // add Dialog
  const dialogOpenHandler = useCallback(() => {
    setNewEquipmentCategoryOpen(true);
  }, []);

  // add new Equipment Category
  const newCategoryDialogHandler = useCallback(
    (type) => () => {
      switch (type) {
        case 'close':
          setNewEquipmentCategoryOpen(false);
          break;
        case 'open':
          break;
        case 'finish':
          setNewEquipmentCategoryOpen(false);
          (async () => {
            await getEquipmentCategoryList();
          })();
          break;
        default:
          break;
      }
    },
    [getEquipmentCategoryList]
  );
  // update Dialog
  const editDialogHandler = useCallback(
    (type) => (value) => {
      switch (type) {
        case 'close':
          setEditLoanRequestDialog();
          break;
        case 'open':
          return () => setEditLoanRequestDialog(value);
        case 'finish':
          setEditLoanRequestDialog();
          (async () => {
            await getEquipmentCategoryList();
          })();
          break;
        default:
          break;
      }
    },
    [getEquipmentCategoryList]
  );

  // 表格数据  update时boundDevice为false可以取消勾选，true不可以取消勾选
  const tableData = useMemo(() => {
    let data = [];
    let wards = [];
    if (categoryListData) {
      setCount(categoryListData.count);
      if (_.isArray(categoryListData.data)) {
        data = _.map(categoryListData.data, (item) => {
          if (item.wards.length) {
            wards = _.map(item.wards, (wardsItem) => {
              if (wardsItem.satelliteStores) {
                return `${wardsItem.name} (${_.map(
                  wardsItem.satelliteStores,
                  (satelliteStoreItem) => satelliteStoreItem.satelliteStoresName
                ).join(', ')})`;
              }
              return wardsItem.name;
            });
          } else {
            wards = [];
          }
          return {
            ...item,
            equipmentCategory: item.categoryName,
            loanModel: item.loanModel,
            loanModelType:
              String(item?.loanModel) === '1' ? 'Central Store only' : 'Central Store with Satellite store',
            loanModelDetail: wards.join(', '),
            thresholdPopup:
              String(item?.loanModel) === '1'
                ? _.map(item.wards, (wardItem) => ({
                    store: wardItem.name,
                    thresholdSettingsDay: item?.adsInfo?.adsReturnThresholdSettings,
                    id: wardItem.name,
                  }))
                : item.wards.reduce((acc, ward) => {
                    if (ward.satelliteStores) {
                      return acc.concat(
                        ward.satelliteStores.map((store) => ({
                          store: `${ward.name}-${store.satelliteStoresName}`,
                          thresholdSettingsDay: store.thresholdSettingsDay,
                          id: store.categorySatelliteStoresId,
                        }))
                      );
                    }
                    return acc;
                  }, []),
            satelliteStore: String(item?.loanModel) !== '1',
            edit: true,
            hospitalCategoryId: item.hospitalCategoryId,
            masterCategoryId: item.masterCategoryId,
            wardIds: _.map(item.wards, (wardIdsItem) => ({
              text: wardIdsItem.name,
              value: wardIdsItem.id,
              boundDevice: wardIdsItem.boundDevice,
            })),
            version: item.version,
            mainWards: item.mainWards.length && item.mainWards[0],
            isCheck: item.isCheck,
            adsInfo: item.adsInfo,
            isReceiptConfirmation: item.isReceiptConfirmation,
            isWardTransfer: item.isWardTransfer,
            lowStockAlert: displayLowStockAlert(item),
            // 提取wards里面的satelliteStores合并到一起，并把wardId注入进去
            satelliteStores: item.wards.reduce((acc, ward) => {
              if (ward.satelliteStores) {
                return acc.concat(ward.satelliteStores.map((store) => ({ ...store, wardId: ward.id })));
              }
              return acc.concat([
                {
                  wardId: ward.id,
                  adsLocation: '',
                  categorySatelliteStoresId: 0,
                  hospitalWardCategoryId: 0,
                  satelliteBoundDevice: false,
                  satelliteStoresName: '',
                  thresholdSettingsDay: 1,
                  satelliteStoresAlertThreshold: 0,
                },
              ]);
            }, []),
            // 提取wards里面的boundDevice为true的设备
            boundDeviceWards: _.filter(item.wards, (ward) => ward.boundDevice),
            isPatientNo: item.isPatientNo,
            loanDurationEnableYn: item.loanDurationEnableYn,
            loanDuration: item.loanDuration,
          };
        });
      }
    }
    return data;
  }, [categoryListData]);
  console.log('tableData', tableData);

  // get Equipment Category list
  const getEquipmentCategorylist = useCallback(async () => {
    try {
      if (!tableParams.corpId) {
        return;
      }

      const { result } = await equipmentAPI.getEquipmentCategory({
        hospitalId: loginInfo.hospitalId,
        wardId: null,
        hospId: null,
      });
      setCategories(result.categories);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo, tableParams.corpId]);

  // get ward list
  const getEquipmentCategoryWardList = useCallback(async () => {
    try {
      if (!tableParams.corpId) {
        return;
      }

      const { result } = await systemAPI.getEquipmentCategoryWardDropdown({ hospId: loginInfo.hospitalId });
      setWardArr(result);
    } catch (error) {
      console.log(error);
    }
  }, [tableParams.corpId]);
  useEffect(() => {
    (async () => {
      await getEquipmentCategorylist();
    })();
    (async () => {
      await getEquipmentCategoryWardList();
    })();
  }, [getEquipmentCategorylist, getEquipmentCategoryWardList]);

  // filter dropdown list
  const filterItemLists = useMemo(() => {
    let categoryList = [];
    if (_.isArray(categories)) {
      categoryList = _.map(categories, (item) => ({ text: item.categoryName, value: item.categoryId }));
    }
    return { categoryList };
  }, [categories]);
  // ward list option
  const wradListOption = useMemo(() => {
    let wardList = [];
    if (_.isArray(wardArr)) {
      wardList = _.map(wardArr, (item) => ({ text: item.name, value: item.id }));
    }
    return { wardList };
  }, [wardArr]);
  // filter
  const filterHandler = (type) => (value) => {
    if (type === 'reset') {
      setFilter(initFilter);
    } else {
      setFilter((preFilter) => ({ ...preFilter, [type]: value }));
    }
  };
  return (
    <div className={classes.root}>
      <Box className={classes.borderB}>
        <Box display="flex" mb="1.313rem" justifyContent="space-between" alignItems="center">
          <Box mt="1.375rem">
            <SearchInput
              placeholder={i18n.t('DUTYASSIGN_ROSTER_SEARCH')}
              inputID="Equipment_search_input"
              buttonID="Equipment_search_button"
              onFocus={inputOnFocus}
              onBlur={inputOnBlur}
              onChange={filterHandler('search')}
              id="setting-equipment-search"
            />
          </Box>
          {roleMap[authNameE.setting_view_equipment] && (
            <Box display="flex" mt="1.375rem">
              <StyleButton
                id="add-equipment-category-button"
                text={i18n.t('SETTING_EQUIPMENT_CATEGORY_BTN')}
                Icon={AddIcon}
                width="12.5rem"
                type="fill"
                iconStyle={{ fontSize: '1rem' }}
                onClick={dialogOpenHandler}
              />
            </Box>
          )}
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb="1rem">
          <Box display="flex" alignItems="center">
            <div className={classes.headText}>{i18n.t('EQUIPMENT_VIEWREQUEST_FILTER')}</div>
            <AutoCompleteMultiCheckbox
              className={classes.wardAutoSelect}
              dropDownIcon={true}
              align="right"
              width="13.75rem"
              height="2.563rem"
              errMsgMargin="3px 0 0 0.825rem"
              errMsgWidth="11rem"
              allText={i18n.t('SETTING_ALL_CATEGORY')}
              itemList={filterItemLists.categoryList}
              value={filter.category}
              onChange={filterHandler('category')}
              id="setting-equipment-category"
              onlySelect={true}
              placeholder={i18n.t('SETTING_SELECT_FROM_BELOW')}
            />
            <AutoCompleteMultiCheckbox
              className={classes.wardAutoSelect}
              dropDownIcon={true}
              align="right"
              width="13.75rem"
              height="2.563rem"
              errMsgMargin="3px 0 0 0.825rem"
              errMsgWidth="11rem"
              allText="All Central Store"
              itemList={wradListOption.wardList}
              value={filter.ward}
              onChange={filterHandler('ward')}
              id="allLoan-model-autoSelect"
              placeholder={i18n.t('SETTING_SELECT_FROM_BELOW')}
            />
            <AutoCompleteMultiCheckbox
              className={classes.wardAutoSelect}
              dropDownIcon={true}
              align="right"
              width="13.75rem"
              height="2.563rem"
              errMsgMargin="3px 0 0 0.825rem"
              errMsgWidth="11rem"
              allText={i18n.t('SETTING_ALL_CRITICALITY')}
              id="setting-criticality-autoSelect"
              onlySelect={true}
              itemList={criticalityCategoryList}
              placeholder={i18n.t('SETTING_SELECT_FROM_BELOW')}
              value={filter.criticality}
              onChange={filterHandler('criticality')}
            />
            <ButtonBase className={classes.reset} onClick={filterHandler('reset')} id="setting-reset-btn">
              {i18n.t('SETTING_RESET')}
            </ButtonBase>
          </Box>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" className={classes.resultsBox}>
        <Results length={count} className={classes.resultsText} />
        <TableUI>
          <TableHead>
            <TableRow>
              <TableCell align="left" style={{ width: '20%' }}>
                {i18n.t('SETTING_EQUIPMENT_CATEGORY_TABLE')}
              </TableCell>
              <TableCell align="left" style={{ width: '12%' }}>
                <Box display="flex" flexDirection="column">
                  <span>{i18n.t('SETTING_LOAN_MODEL_TAVLE')}</span>
                </Box>
              </TableCell>
              <TableCell align="left" style={{ width: '10%' }}>
                CENTRAL STORE (SATELLITE STORE)
              </TableCell>
              <TableCell align="left" style={{ width: '8%' }}>
                {i18n.t('SETTING_CRITICALITY')}
              </TableCell>
              <TableCell align="left" style={{ width: '8%' }}>
                Low Stock Alert
              </TableCell>
              <TableCell align="left" style={{ width: '8%' }}>
                {i18n.t('SETTING_ADS_INTEGRATION_ENABLED')}
              </TableCell>
              <TableCell align="left" style={{ width: '5%' }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData.map((item, index) => (
              <TableRow
                onDoubleClick={editDialogHandler('open')(item)}
                key={`${item.hospitalCategoryId}+${item.masterCategoryId}+${item.version}`}
              >
                <TableCell>
                  <div className={classes.text}> {item.equipmentCategory}</div>
                </TableCell>
                <TableCell>
                  <div className={classes.text}> {item.loanModelType}</div>
                </TableCell>
                <TableCell>
                  <div className={classes.text}> {item.loanModelDetail}</div>
                </TableCell>
                <TableCell>
                  <div className={classes.text}>
                    {item.criticalCategory === 1
                      ? i18n.t('SETTING_CRITICALITY_NORMAL')
                      : i18n.t('SETTING_CRITICALITY_HIGH')}
                  </div>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <div className={classes.text}>{item.lowStockAlert}</div>
                    {item?.adsInfo?.adsReturnThresholdSettings > 0 && (
                      <MultiThresholdButton data={item.thresholdPopup} satelliteStore={item.satelliteStore} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <div className={classes.text}>
                    {isEmpty(item.adsInfo) ? i18n.t('SETTING_INACTIVE') : i18n.t('SETTING_ACTIVE')}
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.text}>
                    {item.edit && roleMap[authNameE.setting_view_equipment] && (
                      <ButtonBase
                        className={classes.editButton}
                        onClick={editDialogHandler('open')(item)}
                        id={`equiment-edit-${index}`}
                      >
                        <EditIcon className={classes.editIcon} />
                      </ButtonBase>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableUI>
      </Box>
      {newEquipmentCategoryOpen && (
        <NewEquipmentCategoryDialog
          dialogCloseHandler={newCategoryDialogHandler('close')}
          dialogFinishHandler={newCategoryDialogHandler('finish')}
          id="new-equipment-category-dialog"
        />
      )}
      {editLoanRequestDialog && (
        <UpdateEquipmentCategoryDialog
          dialogCloseHandler={editDialogHandler('close')}
          dialogFinishHandler={editDialogHandler('finish')}
          data={editLoanRequestDialog}
          id="update-equipment-category-dialog"
        />
      )}
    </div>
  );
};

const displayLowStockAlert = (data) => {
  if (data?.adsInfo?.adsReturnOrderType === '1') {
    return 'OFF';
  } else if (data?.adsInfo?.adsReturnOrderType === '2') {
    return 'Urgent';
  } else if (data?.adsInfo?.adsReturnThresholdSettings > 0) {
    return String(data?.loanModel) === '1' ? 'Threshold Setting' : 'Multi-threshold';
  }

  return '';
};

export default CategoryTab;
